import React, { useEffect, useRef } from 'react';
import maplibregl, { Map, GeoJSONSource } from 'maplibre-gl';
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM, MAP_STYLE_URL } from '../constants';
import type { PropertyPin } from '../types';

interface MapViewProps {
  pins: PropertyPin[];
  polygon: GeoJSON.Polygon | null;
  onPolygonChange: (polygon: GeoJSON.Polygon | null) => void;
  drawing: boolean;
  onDrawingChange: (drawing: boolean) => void;
}

/**
 * Lightweight polygon-draw helper. We don't pull maplibre-gl-draw to keep the
 * bundle small; instead we implement a click-to-add-vertex / double-click-to-
 * finish flow using a single GeoJSON source we update directly.
 */
export default function MapView({
  pins,
  polygon,
  onPolygonChange,
  drawing,
  onDrawingChange,
}: MapViewProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const drawPointsRef = useRef<[number, number][]>([]);
  const drawingRef = useRef(drawing);
  drawingRef.current = drawing;

  // Initialise map once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: MAP_STYLE_URL,
      center: DEFAULT_MAP_CENTER,
      zoom: DEFAULT_MAP_ZOOM,
    });
    mapRef.current = map;

    map.on('load', () => {
      map.addSource('pins', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addSource('polygon', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addSource('draft', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });

      // Filled selection polygon.
      map.addLayer({
        id: 'polygon-fill',
        type: 'fill',
        source: 'polygon',
        paint: { 'fill-color': '#10b981', 'fill-opacity': 0.12 },
      });
      map.addLayer({
        id: 'polygon-line',
        type: 'line',
        source: 'polygon',
        paint: { 'line-color': '#059669', 'line-width': 2 },
      });

      // Draft polygon (during drawing).
      map.addLayer({
        id: 'draft-line',
        type: 'line',
        source: 'draft',
        paint: { 'line-color': '#0ea5e9', 'line-width': 2, 'line-dasharray': [2, 2] },
      });
      map.addLayer({
        id: 'draft-points',
        type: 'circle',
        source: 'draft',
        filter: ['==', '$type', 'Point'],
        paint: { 'circle-radius': 4, 'circle-color': '#0ea5e9', 'circle-stroke-color': '#fff', 'circle-stroke-width': 1 },
      });

      // Pins coloured by move_likelihood.
      map.addLayer({
        id: 'pins-circle',
        type: 'circle',
        source: 'pins',
        paint: {
          'circle-radius': [
            'interpolate', ['linear'], ['get', 'score'],
            0, 3, 100, 8,
          ],
          'circle-color': [
            'interpolate', ['linear'], ['get', 'score'],
            0, '#94a3b8',
            40, '#fbbf24',
            70, '#f97316',
            90, '#ef4444',
          ],
          'circle-opacity': 0.85,
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 1,
        },
      });

      // Click handler — adds a vertex when in drawing mode.
      map.on('click', (e) => {
        if (!drawingRef.current) return;
        drawPointsRef.current = [...drawPointsRef.current, [e.lngLat.lng, e.lngLat.lat]];
        updateDraftLayer();
      });

      // Double click ends drawing.
      map.on('dblclick', (e) => {
        if (!drawingRef.current) return;
        e.preventDefault();
        finishDrawing();
      });
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Push pin updates into the source.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      const src = map.getSource('pins') as GeoJSONSource | undefined;
      if (!src) return;
      src.setData({
        type: 'FeatureCollection',
        features: pins.map((p) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [p.lng, p.lat] },
          properties: { score: p.move_likelihood, uprn: p.uprn, address: p.address_line_1 },
        })),
      });
    };
    if (map.isStyleLoaded()) apply();
    else map.once('load', apply);
  }, [pins]);

  // Push polygon updates into the source.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      const src = map.getSource('polygon') as GeoJSONSource | undefined;
      if (!src) return;
      src.setData({
        type: 'FeatureCollection',
        features: polygon ? [{ type: 'Feature', geometry: polygon, properties: {} }] : [],
      });
    };
    if (map.isStyleLoaded()) apply();
    else map.once('load', apply);
  }, [polygon]);

  function updateDraftLayer() {
    const map = mapRef.current;
    if (!map) return;
    const src = map.getSource('draft') as GeoJSONSource | undefined;
    if (!src) return;
    const points = drawPointsRef.current;
    const features: GeoJSON.Feature[] = points.map((c) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: c },
      properties: {},
    }));
    if (points.length >= 2) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: points },
        properties: {},
      });
    }
    src.setData({ type: 'FeatureCollection', features });
  }

  function finishDrawing() {
    const points = drawPointsRef.current;
    if (points.length >= 3) {
      const ring = [...points, points[0]];
      onPolygonChange({ type: 'Polygon', coordinates: [ring] });
    }
    drawPointsRef.current = [];
    updateDraftLayer();
    onDrawingChange(false);
  }

  function startDrawing() {
    drawPointsRef.current = [];
    updateDraftLayer();
    onPolygonChange(null);
    onDrawingChange(true);
  }

  function clear() {
    drawPointsRef.current = [];
    updateDraftLayer();
    onPolygonChange(null);
    onDrawingChange(false);
  }

  return (
    <div className="relative flex-1 min-h-0">
      <div ref={containerRef} className="absolute inset-0" />

      <div className="absolute top-3 right-3 z-10 flex flex-col gap-2">
        <button
          onClick={startDrawing}
          disabled={drawing}
          className={`px-3 py-2 rounded-lg shadow text-sm font-medium transition-colors ${
            drawing
              ? 'bg-sky-100 text-sky-700 cursor-default'
              : 'bg-white text-slate-700 hover:bg-slate-50'
          }`}
        >
          {drawing ? 'Click on map · double-click to finish' : 'Draw territory'}
        </button>
        {(polygon || drawing) && (
          <button
            onClick={clear}
            className="px-3 py-2 rounded-lg bg-white shadow text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      <div className="absolute bottom-3 right-3 z-10 bg-white shadow rounded-lg px-3 py-2 text-xs text-slate-600">
        <div className="font-semibold text-slate-700 mb-1">Move-likelihood</div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-slate-400"></span>
          <span>0</span>
          <span className="w-3 h-3 rounded-full bg-amber-400 ml-2"></span>
          <span>40</span>
          <span className="w-3 h-3 rounded-full bg-orange-500 ml-2"></span>
          <span>70</span>
          <span className="w-3 h-3 rounded-full bg-rose-500 ml-2"></span>
          <span>90+</span>
        </div>
      </div>
    </div>
  );
}
